<template>
	<div class="px-250-md">
		<h1 class="heading-2 text-align-center">{{ title }}</h1>
		<BaseAlert
			v-if="showExpiredSessionAlert"
			:alert="{ type: 'info', message: ['Your session has expired. Please log in again.'] }"
			class="mt-100 mt-200-md"
		/>
		<BaseAlert
			v-if="showVerifyEmailLoginAlert"
			:alert="{ type: 'info', message: ['Please log in to verify your e-mail address.'] }"
			class="mt-100 mt-200-md"
		/>
		<BaseAlert
			v-if="showThirdPartyAuthErrorAlert"
			:alert="{
				type: 'error',
				message: [
					'We couldn\'t find a Fundrise account with that email. Try logging in with a different Google or Apple account or with an email and password.'
				]
			}"
			data-test="error-alert"
			class="mt-100 mt-200-md"
		/>
		<BaseAlert
			v-if="attemptsRemainingText.length > 0"
			:alert="{ type: 'warning', message: [attemptsRemainingText] }"
			data-test="warning-alert"
			class="mt-100 mt-200-md"
		/>

		<form :class="showThirdPartyAuth ? 'mt-200-smo mt-300-md' : 'my-200-smo my-300-md'" @submit.prevent>
			<div>
				<InputField
					:field="v$.username"
					field-name="Email Address"
					@clear-server="serverValidationHandler.clearServerErrors('username')"
				>
					<template #label>Email address</template>
					<template #input>
						<input
							v-model="v$.username.$model"
							autocomplete="username"
							name="username"
							class="input"
							type="email"
							data-test="usernameInput"
							aria-label="Email address"
						/>
					</template>
				</InputField>
				<InputField
					:field="v$.password"
					field-name="Password"
					class="mt-100"
					@clear-server="serverValidationHandler.clearServerErrors('username')"
				>
					<template #label>Password</template>
					<template #input>
						<input
							v-model="v$.password.$model"
							autocomplete="current-password"
							name="password"
							class="input password-style"
							type="password"
							data-test="passwordInput"
							aria-label="Password"
						/>
					</template>
				</InputField>
				<BaseCtaLink
					data-test="forgot-password-link"
					classes="mt-50 text-decoration-underline body-sm"
					:link="forgotPasswordLink"
				/>
			</div>

			<SubmitButton
				:class="['width-100 mt-100 login-button', { disabled: thirdPartyAuthSubmitted }]"
				:v$="v$"
				:is-working="isLoading"
				data-test="login-submit"
				@sb-click="login()"
				>Log in
			</SubmitButton>
		</form>

		<ThirdPartyAuth
			v-if="showThirdPartyAuth"
			:is-working="thirdPartyAuthSubmitted"
			@google-auth-payload="attemptGoogleLogin"
			@apple-auth-payload="attemptAppleLogin"
		/>

		<div :class="['text-align-center body-sm', { 'mt-200-smo mt-300-md': showThirdPartyAuth }]">
			Not an investor yet?
			<BaseCtaLink classes="ml-25 text-decoration-underline" :link="startInvestingLink" />
		</div>
	</div>
</template>
<script setup lang="ts">
import { AppleAuthParameters, GoogleAuthParameters } from 'types/third-party-auth';
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { email, required } from '@vuelidate/validators';
import { LoginFailureResponse, LoginFailureResponseData, LoginForm } from 'types/authentication';
import { submitAppleLogin, submitGoogleLogin, submitLoginForm } from '@api/login';
import { useRoute, useRouter } from 'vue-router';
import { determineLoginRoute } from '@utils/login-redirects';
import { getItemFromLocalStorage } from '@utils/web-storage';
import InputField from '@components/form/input-field.vue';
import { Link } from 'types/layout';
import { OauthTokenResponse } from 'types/security';
import { pluralize } from '@filters/shared-filters';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import SubmitButton from '@components/form/submit-button.vue';
import ThirdPartyAuth from '@components/third-party-auth/third-party-auth.vue';
import { useAppStore } from '@stores/app';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import { useVuelidate } from '@vuelidate/core';

const serverValidationHandler = ref(new ServerValidationHandler());

const state = reactive({
	username: '',
	password: ''
});

const rules = computed(() => ({
	username: {
		required,
		email,
		serverError: serverValidationHandler.value.serverError('username')
	},
	password: {
		required,
		serverError: serverValidationHandler.value.serverError('username')
	}
}));

const v$ = useVuelidate(rules, state);

const appStore = useAppStore();

const title = 'Log in';

const isLoading = ref(false);
const thirdPartyAuthSubmitted = ref(false);
const attemptsLeft = ref<number | null>(null);
const showThirdPartyAuthErrorAlert = ref(false);

const router = useRouter();
const route = useRoute();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const forgotPasswordLink: Link = {
	text: 'Forgot password?',
	router: 'forgot-password'
};

const startInvestingLink: Link = {
	text: 'Sign up now',
	router: 'checkout-start'
};

const showExpiredSessionAlert = computed((): boolean => {
	return route.query.message === 'expired-session';
});

const showVerifyEmailLoginAlert = computed((): boolean => {
	return route.query.message === 'login-verify';
});

const attemptsRemainingText = computed((): string => {
	if (attemptsLeft.value !== null && attemptsLeft.value > 0 && attemptsLeft.value < 3) {
		return `You have ${attemptsLeft.value} more login ${pluralize(
			attemptsLeft.value,
			'attempt'
		)} before your account is locked.`;
	} else {
		return '';
	}
});

const showThirdPartyAuth = computed((): boolean => {
	return thirdPartyAuthStore.isLoginEnabled;
});

watch(
	() => v$.value.username.$invalid,
	(invalid: boolean) => {
		const passwordStillInvalidFromServer = v$.value.password.$errors.find(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(e: any) => e.$params?.type === 'serverError'
		);
		if (!invalid && passwordStillInvalidFromServer) {
			serverValidationHandler.value.clearServerErrors('password');
			const passVal = v$.value.password.$model;
			v$.value.password.$model = '';
			v$.value.password.$model = passVal;
		}
	}
);

watch(
	() => v$.value.password.$invalid,
	(invalid: boolean) => {
		const usernameStillInvalidFromServer = v$.value.username.$errors.find(
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(e: any) => e.$params?.type === 'serverError'
		);
		if (!invalid && usernameStillInvalidFromServer) {
			serverValidationHandler.value.clearServerErrors('username');
			const usernameVal = v$.value.username.$model;
			v$.value.username.$model = '';
			v$.value.username.$model = usernameVal;
		}
	}
);

onMounted((): void => {
	state.username = getItemFromLocalStorage('emailcapture') || '';

	if (state.username) {
		v$.value.username.$touch();
	}
});

async function constructLoginForm(): Promise<LoginForm> {
	const loginForm: LoginForm = {
		username: state.username,
		password: state.password
	};

	return loginForm;
}

async function setAuthDataAndRedirect(loginResponse: OauthTokenResponse): Promise<void> {
	await appStore.setUserAuthData(loginResponse);
	const destinationRoute = await determineLoginRoute();
	await router.replace(destinationRoute);
}

async function attemptLogin(): Promise<void> {
	const loginForm = await constructLoginForm();
	const loginResponse = await submitLoginForm(loginForm);
	await setAuthDataAndRedirect(loginResponse);
}

async function handleInvalidGrant(loginError: LoginFailureResponseData, isPasswordLogin = true): Promise<void> {
	if (loginError.reason === 'locked_user') {
		await router.replace({ name: 'login-locked-out' });
	}

	if (loginError.reason.includes('bad_credentials') && isPasswordLogin) {
		loginError = Object.assign(loginError, {
			fieldErrorMap: {
				password: ['Username and/or password is incorrect'],
				username: ['Username and/or password is incorrect']
			}
		});

		if (loginError.attempts_left) {
			attemptsLeft.value = parseInt(loginError.attempts_left);
		} else {
			attemptsLeft.value = null;
		}
	} else if (loginError.reason.includes('bad_credentials')) {
		showThirdPartyAuthErrorAlert.value = true;
	}
}

async function login(): Promise<void> {
	isLoading.value = true;

	try {
		await attemptLogin();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		const error = e as LoginFailureResponse;
		const loginError = error.response?.data;

		if (loginError?.error === 'invalid_grant') {
			await handleInvalidGrant(loginError);
		}

		serverValidationHandler.value.handleServerValidation(error);
	} finally {
		isLoading.value = false;
	}
}

async function attemptGoogleLogin(authPayload: GoogleAuthParameters): Promise<void> {
	try {
		thirdPartyAuthSubmitted.value = true;
		const loginResponse = await submitGoogleLogin(authPayload);
		await setAuthDataAndRedirect(loginResponse);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		const error = e as LoginFailureResponse;
		const loginError = error.response?.data;

		if (loginError?.error === 'invalid_grant') {
			await handleInvalidGrant(loginError, false);
		}
	} finally {
		thirdPartyAuthSubmitted.value = false;
	}
}

async function attemptAppleLogin(authPayload: AppleAuthParameters): Promise<void> {
	try {
		thirdPartyAuthSubmitted.value = true;
		const loginResponse = await submitAppleLogin(authPayload);
		await setAuthDataAndRedirect(loginResponse);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		const error = e as LoginFailureResponse;
		const loginError = error.response?.data;

		if (loginError?.error === 'invalid_grant') {
			await handleInvalidGrant(loginError, false);
		}
	} finally {
		thirdPartyAuthSubmitted.value = false;
	}
}
</script>
<style lang="scss" scoped>
@use '../../styles/constants/config.scss' as *;

.password-style {
	letter-spacing: 0.125rem;
}

.login-button {
	&.disabled,
	&[disabled] {
		color: #fff !important;
		background-color: token('brand-secondary') !important;
		border-color: token('brand-secondary') !important;
	}
}
</style>
